import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import { PRIMARY_LICENSE_EXPLAINER } from './PrimaryLicenseChip';
import { US_STATES } from '../profile/profileConstants';
import userService, {
  type CreateUserLicenseDto,
  type UpdateUserLicenseDto,
  type UserLicenseDto,
} from '../../services/userService';

interface LicenseEditorDialogProps {
  open: boolean;
  /** License being edited, or null to create a new one. */
  license: UserLicenseDto | null;
  /** Whether a newly created license should default to primary (user's first license). */
  defaultIsPrimary?: boolean;
  onClose: () => void;
  /** Called with the saved license after a successful create or update. */
  onSaved: (license: UserLicenseDto, created: boolean) => void;
}

function initialForm(license: UserLicenseDto | null, defaultIsPrimary: boolean): CreateUserLicenseDto {
  if (!license) {
    return { stateCode: '', licenseNumber: '', isActive: true, isPrimary: defaultIsPrimary };
  }
  return {
    stateCode: license.stateCode,
    licenseNumber: license.licenseNumber ?? '',
    issueDate: license.issueDate ?? undefined,
    expirationDate: license.expirationDate ?? undefined,
    isActive: license.isActive,
    isPrimary: license.isPrimary,
  };
}

/** Add/edit dialog for a CPA state license. */
export default function LicenseEditorDialog({
  open,
  license,
  defaultIsPrimary = false,
  onClose,
  onSaved,
}: LicenseEditorDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<CreateUserLicenseDto>(() => initialForm(license, defaultIsPrimary));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Reseed the form whenever the dialog opens (render-time seeding, same
  // pattern as LicenseRequirementCard) so a reopened dialog never shows
  // stale values from the previous session.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setForm(initialForm(license, defaultIsPrimary));
      setSaveError('');
    }
  }

  const handleSave = async () => {
    if (!form.stateCode) {
      setSaveError('State is required');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      if (license) {
        const updated = await userService.updateLicense(license.id, form as UpdateUserLicenseDto);
        onSaved(updated, false);
      } else {
        const created = await userService.createLicense(form);
        onSaved(created, true);
      }
    } catch {
      setSaveError('Failed to save license. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{license ? 'Edit license' : 'Add license'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField
            select
            label="State"
            value={form.stateCode}
            onChange={e => setForm(prev => ({ ...prev, stateCode: e.target.value }))}
            fullWidth
            required
          >
            {US_STATES.map(s => (
              <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="License number"
            value={form.licenseNumber || ''}
            onChange={e => setForm(prev => ({ ...prev, licenseNumber: e.target.value }))}
            fullWidth
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">#</InputAdornment> },
            }}
          />
          <TextField
            label="Issue date"
            type="date"
            value={form.issueDate?.toString().substring(0, 10) || ''}
            onChange={e => setForm(prev => ({ ...prev, issueDate: e.target.value }))}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                color="primary"
              />
            }
            label="Active license"
          />
          <Stack spacing={0.25}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPrimary}
                  onChange={e => setForm(prev => ({ ...prev, isPrimary: e.target.checked }))}
                  color="primary"
                />
              }
              label="Set as primary license"
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
              {PRIMARY_LICENSE_EXPLAINER}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.stateCode}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {license ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
